import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Receipt, TrendingUp, TrendingDown, Trash2, Wallet, Tag, ArrowLeft, BadgeCheck, Lock,
} from "lucide-react";
import { api } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";
import CompleteSaleDialog from "../components/CompleteSaleDialog";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";

const resolveUrl = (url) => (url?.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

function saleImage(entry) {
  const scan = entry.captured_image_url ? resolveUrl(entry.captured_image_url) : null;
  const match = entry.reference_image_url || null;
  const wantScan = (entry.display_image || (match ? "match" : "scan")) === "scan";
  return wantScan ? (scan || match) : (match || scan);
}

function SummaryCard({ testId, icon: Icon, label, value, accent, sub }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-[#111113] rounded-xl p-5">
      <Icon className="w-5 h-5 mb-2" style={{ color: accent }} />
      <p className="text-2xl font-display font-bold font-mono-plex" style={{ color: accent }}>{value}</p>
      <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-zinc-600 mt-1">{sub}</p>}
    </div>
  );
}

export default function SalesLedger() {
  const { format } = useCurrency();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [dialogCard, setDialogCard] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = () => {
    api.get("/sales?limit=50").then((res) => setData(res.data)).catch(() => setData({ sales: [], total: 0, summary: { count: 0, total_sold: 0, total_market: 0, net: 0 } }));
    api.get("/cards").then((res) => setPending(res.data.filter((c) => c.sale_status))).catch(() => {});
  };
  useEffect(load, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await api.get(`/sales?skip=${data.sales.length}&limit=50`);
      setData((prev) => ({ ...res.data, sales: [...prev.sales, ...res.data.sales] }));
    } catch {
      toast.error("Couldn't load more sales");
    } finally {
      setLoadingMore(false);
    }
  };

  const summary = data?.summary;
  const net = summary?.net ?? 0;
  const netPositive = net >= 0;

  const removeEntry = async (sale) => {
    if (!window.confirm(`Remove "${sale.name}" from your sales ledger? This can't be undone.`)) return;
    try {
      await api.delete(`/sales/${sale.id}`);
      setData((prev) => {
        const sales = prev.sales.filter((s) => s.id !== sale.id);
        const total_sold = Math.round((prev.summary.total_sold - (sale.sold_price || 0)) * 100) / 100;
        const total_market = Math.round((prev.summary.total_market - (sale.market_value || 0)) * 100) / 100;
        return {
          ...prev,
          sales,
          total: Math.max(0, (prev.total ?? prev.sales.length) - 1),
          summary: { count: prev.summary.count - 1, total_sold, total_market, net: Math.round((total_sold - total_market) * 100) / 100 },
        };
      });
      toast.success("Ledger entry removed");
    } catch {
      toast.error("Couldn't remove the entry");
    }
  };

  const pendingSorted = useMemo(
    () => [...pending].sort((a, b) => (a.sale_status === "sold_stc" ? -1 : 1) - (b.sale_status === "sold_stc" ? -1 : 1)),
    [pending]
  );

  if (!data) {
    return <div className="min-h-screen bg-[#05060f] flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-[#A78BFA] rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#05060f] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/binder" data-testid="sales-back-link" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
        <h1 data-testid="sales-ledger-title" className="font-display text-4xl sm:text-5xl font-bold mb-2 flex items-center gap-3">
          <Receipt className="w-8 h-8 text-emerald-400" /> Sales Ledger
        </h1>
        <p className="text-zinc-400 text-sm mb-8 flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-zinc-600" /> Private — only you can see what you've sold and for how much.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <SummaryCard testId="sales-total-revenue" icon={Wallet} label="Total sales revenue" accent="#34D399" value={format(summary.total_sold) ?? "$0.00"} />
          <SummaryCard testId="sales-total-market" icon={Tag} label="Market value of cards sold" accent="#F59E0B" value={format(summary.total_market) ?? "$0.00"} />
          <SummaryCard
            testId="sales-net"
            icon={netPositive ? TrendingUp : TrendingDown}
            label="Net vs market value"
            accent={netPositive ? "#34D399" : "#F87171"}
            value={`${netPositive ? "+" : "−"}${format(Math.abs(net)) ?? "$0.00"}`}
            sub={netPositive ? "You sold above market — nice." : "Sold below current market value."}
          />
        </div>

        {pendingSorted.length > 0 && (
          <section data-testid="sales-pending-section" className="mb-10">
            <h2 className="text-base md:text-lg font-semibold mb-1">Pending sales</h2>
            <p className="text-xs text-zinc-500 mb-4">Cards in your binder marked Under Offer or Sold STC. Once the money lands, complete the sale to log it here.</p>
            <div className="space-y-2">
              {pendingSorted.map((card) => {
                const img = saleImage(card);
                const price = card.sale_status === "sold_stc" ? card.sold_price : card.offer_price;
                const sold = card.sale_status === "sold_stc";
                return (
                  <div key={card.id} data-testid={`sales-pending-${card.id}`} className="flex items-center gap-3 border border-white/10 bg-[#111113] rounded-xl p-3">
                    {img ? (
                      <img src={pcImageUrl(img, "240")} onError={pcImageFallback} alt={card.name} className="w-10 h-14 object-cover rounded-md border border-white/10 shrink-0" />
                    ) : (
                      <div className="w-10 h-14 rounded-md border border-white/10 bg-black/40 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <Link to={`/card/${card.id}`} className="text-sm font-semibold text-white hover:text-[#A78BFA] transition-colors truncate block">{card.name}</Link>
                      <p className="text-[11px] text-zinc-500 truncate">{card.set_name || "Unknown set"}</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold shrink-0 ${sold ? "bg-red-600/20 text-red-300 border border-red-500/30" : "bg-[#7C3AED]/20 text-[#A78BFA] border border-[#7C3AED]/30"}`}>
                      {sold ? "Sold STC" : "Under Offer"}
                    </span>
                    <span className="font-mono-plex text-sm font-semibold text-zinc-300 w-20 text-right shrink-0">{price != null ? format(price) : "—"}</span>
                    <Button
                      data-testid={`sales-complete-${card.id}`}
                      onClick={() => setDialogCard(card)}
                      size="sm"
                      className="rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/25 shrink-0"
                      variant="ghost"
                    >
                      <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Complete
                    </Button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section>
          <h2 className="text-base md:text-lg font-semibold mb-1">Completed sales {summary.count > 0 && <span className="text-xs text-zinc-500 font-normal">({summary.count})</span>}</h2>
          <p className="text-xs text-zinc-500 mb-4">Every card you've sold, what it went for, and how that compared to market value on the day.</p>
          {data.sales.length === 0 ? (
            <div data-testid="sales-empty" className="border border-dashed border-white/10 rounded-2xl p-10 text-center">
              <Receipt className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="text-sm text-zinc-400 mb-1">No sales logged yet.</p>
              <p className="text-xs text-zinc-600">Mark a card "Sold STC" on its detail page, then hit "Complete sale" when the deal is done.</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {data.sales.map((sale) => {
                const img = saleImage(sale);
                const diff = sale.market_value != null ? sale.sold_price - sale.market_value : null;
                const up = diff != null && diff >= 0;
                return (
                  <div key={sale.id} data-testid={`sales-entry-${sale.id}`} className="flex items-center gap-3 border border-white/10 bg-[#111113] rounded-xl p-3 group">
                    {img ? (
                      <img src={pcImageUrl(img, "240")} onError={pcImageFallback} alt={sale.name} className="w-10 h-14 object-cover rounded-md border border-white/10 shrink-0" />
                    ) : (
                      <div className="w-10 h-14 rounded-md border border-white/10 bg-black/40 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white truncate">
                        {sale.name}
                        {sale.quantity > 1 && <span className="text-zinc-500 font-normal"> ×{sale.quantity}</span>}
                      </p>
                      <p className="text-[11px] text-zinc-500 truncate">
                        {[sale.set_name, sale.sold_at && new Date(sale.sold_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div className="text-right shrink-0 hidden sm:block">
                      <p className="text-[10px] uppercase tracking-widest text-zinc-600">Market</p>
                      <p className="font-mono-plex text-xs text-zinc-400">{sale.market_value != null ? format(sale.market_value) : "—"}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] uppercase tracking-widest text-zinc-600">Sold for</p>
                      <p data-testid={`sales-entry-price-${sale.id}`} className="font-mono-plex text-sm font-semibold text-emerald-300">{format(sale.sold_price)}</p>
                    </div>
                    {diff != null && (
                      <span
                        data-testid={`sales-entry-diff-${sale.id}`}
                        className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-mono-plex font-semibold px-2 py-1 rounded-full ${up ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}
                      >
                        {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {up ? "+" : "−"}{format(Math.abs(diff))}
                      </span>
                    )}
                    <button
                      data-testid={`sales-entry-delete-${sale.id}`}
                      onClick={() => removeEntry(sale)}
                      title="Remove this ledger entry"
                      className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-zinc-600 opacity-0 group-hover:opacity-100 hover:text-red-400 hover:bg-red-500/10 transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
              </div>
              {data.total > data.sales.length && (
                <div className="text-center mt-4">
                  <Button
                    data-testid="sales-load-more"
                    variant="outline"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="border-white/20 hover:bg-white/5 rounded-full px-6"
                  >
                    {loadingMore ? "Loading..." : `Load more (${data.total - data.sales.length} remaining)`}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>

        <CompleteSaleDialog
          card={dialogCard}
          open={!!dialogCard}
          onOpenChange={(open) => !open && setDialogCard(null)}
          onCompleted={(cardId) => {
            setPending((prev) => prev.filter((c) => c.id !== cardId));
            load();
          }}
        />
      </div>
    </div>
  );
}
